import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, CameraOff, Keyboard, RefreshCw } from "lucide-react";

const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF, Html5QrcodeSupportedFormats.QR_CODE,
];
const REGION_ID = "bk-barcode-reader";

export function BarcodeScanner({ onScan, paused }) {
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", at: 0 });
  const [state, setState] = useState("idle"); // idle | starting | running | denied | error | nocamera
  const [errMsg, setErrMsg] = useState("");
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try { await s.stop(); } catch { /* not running */ }
    try { s.clear(); } catch { /* noop */ }
  };

  const start = async () => {
    setErrMsg("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setState("nocamera");
      setErrMsg("This browser can't access the camera. Use the manual entry below.");
      return;
    }
    setState("starting");
    try {
      // Explicit permission request first so the browser prompt appears immediately
      // and a denial gives us a clear error instead of a silent black box.
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      setState(e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "denied" : "nocamera");
      setErrMsg(
        e?.name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access for this site in your browser settings, then tap Retry."
          : "No camera found. You can still type the barcode below.",
      );
      return;
    }
    try {
      await stop();
      const s = new Html5Qrcode(REGION_ID, { formatsToSupport: FORMATS, verbose: false });
      scannerRef.current = s;
      await s.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (w, h) => ({ width: Math.min(w * 0.9, 340), height: Math.min(h * 0.45, 180) }), aspectRatio: 1.333 },
        (text) => {
          const now = Date.now();
          if (text === lastRef.current.code && now - lastRef.current.at < 2500) return;
          lastRef.current = { code: text, at: now };
          if (navigator.vibrate) navigator.vibrate(60);
          onScan(text.trim());
        },
        () => {},
      );
      setState("running");
    } catch (e) {
      setState("error");
      setErrMsg(String(e?.message || e || "Couldn't start the camera."));
    }
  };

  useEffect(() => {
    if (!paused) start();
    else stop();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  const submitManual = (e) => {
    e.preventDefault();
    const v = manual.trim();
    if (!v) return;
    onScan(v);
    setManual("");
  };

  return (
    <div className="space-y-3" data-testid="barcode-scanner">
      <div className="relative bg-stone-900 rounded-2xl overflow-hidden min-h-[220px]">
        <div id={REGION_ID} className="w-full [&_video]:w-full [&_video]:rounded-2xl" />
        {state !== "running" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-6 gap-3" data-testid="scanner-overlay">
            {state === "starting" ? (
              <>
                <RefreshCw className="w-8 h-8 animate-spin text-brand-yellow" />
                <div className="text-xs uppercase tracking-widest font-black">Starting camera…</div>
              </>
            ) : (
              <>
                {state === "denied" || state === "nocamera" || state === "error" ? <CameraOff className="w-8 h-8 text-brand-yellow" /> : <Camera className="w-8 h-8 text-brand-yellow" />}
                <div className="text-sm font-semibold max-w-xs">{errMsg || (paused ? "Scanner paused" : "Point the camera at a barcode")}</div>
                {!paused && (
                  <button type="button" onClick={start} data-testid="scanner-retry-btn"
                    className="bg-white text-brand-red px-4 py-2 rounded-full text-[11px] uppercase tracking-widest font-black active:scale-95">
                    {state === "idle" ? "Start camera" : "Retry"}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <button type="button" onClick={() => setShowManual((v) => !v)} data-testid="scanner-manual-toggle"
        className="text-xs font-bold text-stone-600 flex items-center gap-1.5 hover:text-brand-red">
        <Keyboard className="w-4 h-4" /> {showManual ? "Hide manual entry" : "Type a barcode instead"}
      </button>
      {showManual && (
        <form onSubmit={submitManual} className="flex gap-2" data-testid="scanner-manual-form">
          <input value={manual} onChange={(e) => setManual(e.target.value)} inputMode="numeric" placeholder="Barcode number" data-testid="scanner-manual-input"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-stone-200 bg-white font-mono outline-none focus:border-brand-red" />
          <button type="submit" data-testid="scanner-manual-submit" className="bg-brand-red text-white px-4 rounded-xl text-xs uppercase tracking-widest font-black">Go</button>
        </form>
      )}
    </div>
  );
}
